import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useAllQuizzes, useQuizAttempts } from '@/hooks/useQuizzes'
import { useQuiz } from '@/hooks/useQuizzes'
import { useLesson, useModule, useCourse } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import { QuizAttemptReviewModal } from '@/components/quiz/QuizAttemptReviewModal'
import type { QuizAttemptWithStudent, QuizWithLessonInfo } from '@/types/quiz'

type SortOption = 'newest' | 'oldest' | 'name_asc' | 'name_desc'
type ViewMode = 'courses' | 'quizzes'

export const InstructorQuizReviewPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedAttempt, setSelectedAttempt] = useState<QuizAttemptWithStudent | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('courses')
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null)

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const overviewPath = isAdmin ? '/admin' : '/instructor'

  const isQuizDetailMode = !!quizId

  // Reset course selection when navigating back to quiz list (no quizId)
  useEffect(() => {
    if (!quizId) {
      setViewMode('courses')
      setSelectedCourseId(null)
      setSearchQuery('')
    }
  }, [quizId])

  const { data: allQuizzes, isLoading: quizzesLoading } = useAllQuizzes()
  const { data: quiz, isLoading: quizLoading } = useQuiz(quizId || '')
  const { data: attempts, isLoading: attemptsLoading } = useQuizAttempts(quizId || '', statusFilter)
  const { data: lesson } = useLesson(quiz?.lessonId || '')
  const { data: module } = useModule(lesson?.moduleId || '')
  const { data: course } = useCourse(module?.courseId || '')

  const coursesMap = useMemo(() => {
    if (!allQuizzes) return []
    const map = new Map<string, { courseId: string; courseTitle: string; quizzes: QuizWithLessonInfo[]; totalPending: number }>()
    for (const quiz of allQuizzes) {
      const key = quiz.courseId || 'unknown'
      if (!map.has(key)) {
        map.set(key, { courseId: key, courseTitle: quiz.courseTitle || 'Unknown Course', quizzes: [], totalPending: 0 })
      }
      const entry = map.get(key)!
      entry.quizzes.push(quiz)
      entry.totalPending += quiz.pendingCount
    }
    return Array.from(map.values()).sort((a, b) => b.totalPending - a.totalPending || a.courseTitle.localeCompare(b.courseTitle))
  }, [allQuizzes])

  const courseQuizzes = useMemo(() => {
    if (!allQuizzes || !selectedCourseId) return []
    let list = allQuizzes.filter((q) => q.courseId === selectedCourseId)
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (quiz) =>
          quiz.title.toLowerCase().includes(q) ||
          quiz.lessonTitle.toLowerCase().includes(q) ||
          quiz.moduleTitle.toLowerCase().includes(q)
      )
    }
    switch (sortBy) {
      case 'newest':
        return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      case 'oldest':
        return list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      case 'name_asc':
        return list.sort((a, b) => a.title.localeCompare(b.title))
      case 'name_desc':
        return list.sort((a, b) => b.title.localeCompare(a.title))
      default:
        return list
    }
  }, [allQuizzes, selectedCourseId, searchQuery, sortBy])

  const sortedAttempts = useMemo(() => {
    if (!attempts) return []
    const list = [...attempts]
    switch (sortBy) {
      case 'newest':
        return list.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
      case 'oldest':
        return list.sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
      case 'name_asc':
        return list.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''))
      case 'name_desc':
        return list.sort((a, b) => (b.studentName || '').localeCompare(a.studentName || ''))
      default:
        return list
    }
  }, [attempts, sortBy])

  const handleReview = (attempt: QuizAttemptWithStudent) => {
    setSelectedAttempt(attempt)
    setShowModal(true)
  }

  const handleGraded = () => {
    setShowModal(false)
    setSelectedAttempt(null)
  }

  const selectedCourseName = coursesMap.find((c) => c.courseId === selectedCourseId)?.courseTitle || 'Course'

  // ========== QUIZ DETAIL MODE ==========
  if (isQuizDetailMode) {
    const totalAttempts = attempts?.length || 0
    const needsReview = attempts?.filter((a) => a.status === 'needs_review').length || 0
    const graded = attempts?.filter((a) => a.status === 'graded').length || 0

    if (quizLoading || attemptsLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-sm font-bold text-slate-500 dark:text-slate-400">Loading quiz attempts...</p>
          </div>
        </div>
      )
    }

    return (
      <div className="max-w-[1600px] mx-auto">
        <PageHeader
          title={`${quiz?.title || 'Quiz'} - Essay Review`}
          subtitle="Review and grade essay answers from students"
          badge={`${quiz?.passingScore || 0}% passing`}
          badgeVariant="blue"
          breadcrumbs={
            isAdmin
              ? [
                  { label: 'Quiz Reviews', to: `${overviewPath}/quizzes` },
                  { label: course?.title || 'Course' },
                  { label: 'Essay Review' }
                ]
              : [
                  { label: 'Quiz Reviews', to: `${overviewPath}/quizzes` },
                  { label: course?.title || 'Course' },
                  { label: module?.title || 'Module' },
                  { label: 'Essay Review' }
                ]
          }
          actions={
            <button
              onClick={() => navigate(`${overviewPath}/quizzes`)}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-slate-200 dark:shadow-none"
            >
              <span className="material-symbols-outlined text-[18px]">arrow_back</span>
              Semua Kuis
            </button>
          }
        />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center justify-center size-9 rounded-lg bg-white/20 backdrop-blur-md">
                <span className="material-symbols-outlined text-[20px]">quiz</span>
              </div>
              <p className="text-white/80 text-xs font-black uppercase tracking-wider">Total Percobaan</p>
            </div>
            <p className="text-2xl font-black">{totalAttempts}</p>
          </div>
          <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center justify-center size-9 rounded-lg bg-white/20 backdrop-blur-md">
                <span className="material-symbols-outlined text-[20px]">pending</span>
              </div>
              <p className="text-white/80 text-xs font-black uppercase tracking-wider">Perlu Review</p>
            </div>
            <p className="text-2xl font-black">{needsReview}</p>
          </div>
          <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md">
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center justify-center size-9 rounded-lg bg-white/20 backdrop-blur-md">
                <span className="material-symbols-outlined text-[20px]">check_circle</span>
              </div>
              <p className="text-white/80 text-xs font-black uppercase tracking-wider">Sudah Dinilai</p>
            </div>
            <p className="text-2xl font-black">{graded}</p>
          </div>
        </div>

        {/* Filter + Sort Bar */}
        <div className="bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-xl">
              {[
                { value: 'all', label: 'Semua' },
                { value: 'needs_review', label: 'Perlu Review' },
                { value: 'graded', label: 'Sudah Dinilai' },
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                    statusFilter === filter.value
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Urutkan:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-4 py-2 text-sm font-bold rounded-xl border border-slate-100 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-primary/50 focus:ring-0 outline-none transition-all"
              >
                <option value="newest">Terbaru</option>
                <option value="oldest">Terlama</option>
                <option value="name_asc">Nama A-Z</option>
                <option value="name_desc">Nama Z-A</option>
              </select>
            </div>
          </div>
        </div>

        {/* Attempts List */}
        <div className="space-y-3">
          {sortedAttempts.length > 0 ? (
            sortedAttempts.map((attempt) => (
              <div
                key={attempt.id}
                className="rounded-xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 p-4 shadow-sm hover:shadow-md transition-all group"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="size-9 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-black text-xs shadow-md shrink-0">
                      {attempt.studentName ? attempt.studentName.charAt(0).toUpperCase() : 'S'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-slate-900 dark:text-white text-sm group-hover:text-primary transition-colors truncate">{attempt.studentName}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 font-bold truncate">{attempt.studentEmail}</div>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 font-bold shrink-0">
                      <span>#{attempt.attemptNumber}</span>
                      <span className="text-slate-300 dark:text-slate-600">|</span>
                      <span>Skor: {attempt.score}%</span>
                      <span className="text-slate-300 dark:text-slate-600">|</span>
                      <span>{new Date(attempt.startedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold shrink-0 ${
                      attempt.status === 'graded'
                        ? 'bg-emerald-500 text-white'
                        : attempt.status === 'needs_review'
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                          : 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-white/10'
                    }`}>
                      {attempt.status === 'needs_review' ? 'Review' : attempt.status === 'graded' ? 'Dinilai' : 'Submitted'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleReview(attempt)}
                    className={`px-4 py-2 rounded-xl text-xs font-black whitespace-nowrap transition-all ${
                      attempt.status === 'graded'
                        ? 'bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10'
                        : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 hover:opacity-90 shadow-lg shadow-slate-200 dark:shadow-none'
                    }`}
                  >
                    {attempt.status === 'graded' ? 'Detail' : 'Review'}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-dashed border-slate-200 dark:border-white/10 py-16 px-8 text-center backdrop-blur-sm">
              <div className="size-16 bg-slate-100 dark:bg-white/5 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="material-symbols-outlined text-[32px] text-slate-300 dark:text-slate-600">quiz</span>
              </div>
              <p className="text-slate-900 dark:text-white font-black text-lg mb-2">Belum Ada Percobaan Kuis</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-bold">
                {statusFilter === 'all'
                  ? 'Belum ada siswa yang mengerjakan kuis ini.'
                  : `Tidak ada percobaan dengan status ${statusFilter === 'needs_review' ? 'perlu review' : 'sudah dinilai'}.`
                }
              </p>
            </div>
          )}
        </div>

        {/* Review Modal */}
        {showModal && selectedAttempt && (
          <QuizAttemptReviewModal
            attempt={selectedAttempt}
            onClose={() => {
              setShowModal(false)
              setSelectedAttempt(null)
            }}
            onGraded={handleGraded}
          />
        )}
      </div>
    )
  }

  // ========== COURSE LIST / QUIZ LIST MODE ==========
  if (quizzesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-sm font-bold text-slate-500 dark:text-slate-400">Loading quizzes...</p>
        </div>
      </div>
    )
  }

  // COURSE LIST VIEW
  if (viewMode === 'courses' || !selectedCourseId) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <PageHeader
          title="Quiz Reviews"
          subtitle="Pilih kursus untuk melihat dan menilai essay kuis"
          breadcrumbs={
            isAdmin
              ? [{ label: 'Quiz Reviews' }]
              : [{ label: 'Quiz Reviews' }]
          }
        />

        {/* Search */}
        <div className="bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm mb-6">
          <div className="relative max-w-md">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">search</span>
            <input
              type="text"
              placeholder="Cari kursus..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent focus:border-primary/30 focus:bg-white rounded-2xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 outline-none transition-all"
            />
          </div>
        </div>

        {/* Course Cards */}
        <div className="space-y-3">
          {coursesMap.length > 0 ? (
            coursesMap
              .filter((c) => !searchQuery || c.courseTitle.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((courseItem) => (
                <div
                  key={courseItem.courseId}
                  onClick={() => {
                    setSelectedCourseId(courseItem.courseId)
                    setViewMode('quizzes')
                    setSearchQuery('')
                  }}
                  className="group bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-white/5 p-5 hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-16 h-16 bg-primary/5 rounded-full -mr-8 -mt-8 transition-all group-hover:scale-150"></div>
                  <div className="flex items-center justify-between gap-4 relative">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="size-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shrink-0">
                        <span className="material-symbols-outlined text-[22px]">school</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-black text-slate-900 dark:text-white group-hover:text-primary transition-colors">{courseItem.courseTitle}</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-bold">
                          {courseItem.quizzes.length} quiz{courseItem.quizzes.length !== 1 ? 'es' : ''}
                        </p>
                      </div>
                      {courseItem.totalPending > 0 && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0">
                          <span className="material-symbols-outlined text-[14px]">pending</span>
                          {courseItem.totalPending} pending
                        </span>
                      )}
                    </div>
                    <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-[20px] group-hover:text-primary transition-colors">arrow_forward</span>
                  </div>
                </div>
              ))
          ) : (
            <div className="rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-dashed border-slate-200 dark:border-white/10 py-16 px-8 text-center backdrop-blur-sm">
              <div className="size-16 bg-slate-100 dark:bg-white/5 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="material-symbols-outlined text-[32px] text-slate-300 dark:text-slate-600">school</span>
              </div>
              <p className="text-slate-900 dark:text-white font-black text-lg mb-2">Tidak Ada Kursus</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-bold">
                {searchQuery ? 'Tidak ada kursus yang sesuai dengan pencarian.' : 'Belum ada kursus dengan kuis.'}
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  // QUIZ LIST VIEW (inside selected course)
  return (
    <div className="max-w-[1600px] mx-auto">
      <PageHeader
        title="Quiz Reviews"
        subtitle={`Kuis di ${selectedCourseName}`}
        breadcrumbs={
          isAdmin
            ? [
                { label: 'Quiz Reviews', to: `${overviewPath}/quizzes` },
                { label: selectedCourseName }
              ]
            : [
                { label: 'Quiz Reviews', to: `${overviewPath}/quizzes` },
                { label: selectedCourseName }
              ]
        }
        actions={
          <button
            onClick={() => {
              setSelectedCourseId(null)
              setViewMode('courses')
              setSearchQuery('')
            }}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white rounded-xl font-bold text-sm hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Semua Kursus
          </button>
        }
      />

      {/* Search + Sort */}
      <div className="bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="relative max-w-md flex-1">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">search</span>
            <input
              type="text"
              placeholder="Cari kuis..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent focus:border-primary/30 focus:bg-white rounded-2xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Urutkan:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="px-4 py-2 text-sm font-bold rounded-xl border border-slate-100 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-primary/50 focus:ring-0 outline-none transition-all"
            >
              <option value="newest">Terbaru</option>
              <option value="oldest">Terlama</option>
              <option value="name_asc">Nama A-Z</option>
              <option value="name_desc">Nama Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* Quizzes List */}
      <div className="space-y-3">
        {courseQuizzes.length > 0 ? (
          courseQuizzes.map((quizItem) => (
            <div
              key={quizItem.id}
              onClick={() => navigate(`${overviewPath}/quizzes/${quizItem.id}`)}
              className="group bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-white/5 p-5 hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-16 h-16 bg-primary/5 rounded-full -mr-8 -mt-8 transition-all group-hover:scale-150"></div>
              <div className="flex items-center justify-between gap-4 relative">
                <div className="flex items-center gap-3 flex-1">
                  <div className="size-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-md shrink-0">
                    <span className="material-symbols-outlined text-[22px]">quiz</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-black text-slate-900 dark:text-white group-hover:text-primary transition-colors truncate">{quizItem.title}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-bold truncate">
                      {quizItem.moduleTitle} &rsaquo; {quizItem.lessonTitle}
                    </p>
                  </div>
                  {quizItem.isPublished ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white shrink-0">
                      <span className="material-symbols-outlined text-[14px]">check_circle</span>
                      Published
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-black rounded-lg bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10 shrink-0">
                      Draft
                    </span>
                  )}
                </div>
                <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-[20px] group-hover:text-primary transition-colors">arrow_forward</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400 font-bold ml-[52px] mt-2">
                <span className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">people</span>
                  {quizItem.attemptCount} percobaan
                </span>
                {quizItem.pendingCount > 0 && (
                  <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <span className="material-symbols-outlined text-[14px]">pending</span>
                    {quizItem.pendingCount} pending
                  </span>
                )}
                <span className="text-slate-300 dark:text-slate-600">|</span>
                <span>{quizItem.passingScore}% lulus</span>
                {quizItem.timeLimitMinutes > 0 && (
                  <>
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span>{quizItem.timeLimitMinutes}m</span>
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-dashed border-slate-200 dark:border-white/10 py-16 px-8 text-center backdrop-blur-sm">
            <div className="size-16 bg-slate-100 dark:bg-white/5 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="material-symbols-outlined text-[32px] text-slate-300 dark:text-slate-600">quiz</span>
            </div>
            <p className="text-slate-900 dark:text-white font-black text-lg mb-2">Tidak Ada Kuis</p>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-bold">
              {searchQuery ? 'Tidak ada kuis yang sesuai dengan pencarian.' : 'Belum ada kuis di kursus ini.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
